/**
 * OrganizationsDNS — React Query service layer
 * All fetchers and mutations for the module live here.
 * Token is accepted as a parameter so callers can pass it from Redux state.
 */

import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DNS_ADD_UPDATE_ORG, DNS_FOLDER_MASTER, DNS_GET_ALL_ORGS, DNS_GET_FOLDER_TREE, DNS_DELETE_ORG, DNS_GET_UPLOADS, DNS_MOVE_UPLOAD, DNS_COPY_UPLOAD, DNS_UPLOAD_FILES, DNS_RENAME_UPLOAD, DNS_DELETE_UPLOAD, DNS_UPDATE_UPLOAD_LOCK, DNS_UPDATE_UPLOAD_STATUS, DNS_UPDATE_UPLOAD_TAGS, DNS_DELETE_FOREVER_ORG, DNS_ASSIGN_USER, DNS_GET_USERS, DNS_SEARCH_DETAILS, DNS_GET_ALL_TRASH_DATA, DNS_DELETE_FOREVER_UPLOAD } from "../Common/API";

/* ─── Query Key Factory ─────────────────────────────── */
export const dnsKeys = {
  allOrgs: () => ["dns", "orgs"],
  folderTree: (orgId) => ["dns", "folderTree", orgId],
};

/* ─── Fetchers (pure async functions) ───────────────── */

export const fetchAllOrgs = async (token) => {
  const { data } = await axios.get(DNS_GET_ALL_ORGS, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data?.data ?? [];
};

export const fetchFolderTree = async (token, orgId) => {
  const { data } = await axios.get(`${DNS_GET_FOLDER_TREE}?OrganizationsDNSId=${orgId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data?.data ?? { model: [], organizations: [] };
};

/* ─── Mutations (POST calls) ────────────────────────── */

export const addUpdateOrg = async ({ token, payload }) => {
  const { data } = await axios.post(DNS_ADD_UPDATE_ORG, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

export const addUpdateFolder = async ({ token, payload }) => {
  const { data } = await axios.post(DNS_FOLDER_MASTER, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

export const deleteOrg = async ({ token, id, isDeleted = true }) => {
  const { data } = await axios.get(`${DNS_DELETE_ORG}?OrganizationsDNSIds=${id}&IsDeleted=${isDeleted}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

export const deleteForeverOrg = async ({ token, id }) => {
  const { data } = await axios.get(`${DNS_DELETE_FOREVER_ORG}?OrganizationsDNSIds=${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

/* ─── Hooks ─────────────────────────────────────────── */

/** Fetch all organizations */
export const useGetAllOrgs = (token) =>
  useQuery({
    queryKey: dnsKeys.allOrgs(),
    queryFn: () => fetchAllOrgs(token),
    enabled: !!token,
    staleTime: 1000 * 60 * 5, // 5 min cache — organizations rarely change
    select: (data) => data,
  });

/** Fetch folder tree for the selected org */
export const useGetFolderTree = (token, orgId) =>
  useQuery({
    queryKey: dnsKeys.folderTree(orgId),
    queryFn: () => fetchFolderTree(token, orgId),
    enabled: !!token && !!orgId,
    staleTime: 1000 * 60 * 2, // 2 min — folders change more often
  });

/** Add or update an organization */
export const useAddUpdateOrg = (token) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => addUpdateOrg({ token, payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dnsKeys.allOrgs() });
    },
  });
};

/** Add or update a folder */
export const useAddUpdateFolder = (token, orgId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => addUpdateFolder({ token, payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dnsKeys.folderTree(orgId) });
      qc.invalidateQueries({ queryKey: ["dns", "uploads", orgId] });
    },
  });
};

/** Delete an organization */
export const useDeleteOrg = (token) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isDeleted }) => deleteOrg({ token, id, isDeleted }),
    onSuccess: (_data, variables) => {
      const { id, isDeleted } = variables ?? {};
      // Optimistically flip isDeleted so Folder Structure clears before refetch returns
      if (id != null) {
        qc.setQueryData(dnsKeys.allOrgs(), (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((o) =>
            o.organizationsDNSId === id ? { ...o, isDeleted: !!isDeleted } : o
          );
        });
        qc.removeQueries({ queryKey: dnsKeys.folderTree(id) });
      }
      qc.invalidateQueries({ queryKey: dnsKeys.allOrgs() });
    },
  });
};

/** Delete organization forever (permanent delete) */
export const useDeleteForeverOrg = (token) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteForeverOrg({ token, id }),
    onSuccess: (_data, id) => {
      if (id != null) {
        qc.setQueryData(dnsKeys.allOrgs(), (old) => {
          if (!Array.isArray(old)) return old;
          return old.filter((o) => o.organizationsDNSId !== id);
        });
        qc.removeQueries({ queryKey: dnsKeys.folderTree(id) });
      }
      qc.invalidateQueries({ queryKey: dnsKeys.allOrgs() });
    },
  });
};

/* ─── Fetcher for Uploads by Parent ─────────────────── */
export const fetchUploadsByParent = async (token, orgId, parentId) => {
  const { data } = await axios.get(
    `${DNS_GET_UPLOADS}?OrganizationsDNSId=${orgId}&ParentID=${parentId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data?.data ?? [];
};

/* ─── Mutation for Move Upload ──────────────────────── */
export const moveUpload = async ({ token, type, uploadId, newParentId }) => {
  const { data } = await axios.post(
    `${DNS_MOVE_UPLOAD}?Type=${type}&UploadID=${uploadId}&NewParentID=${newParentId}`, null,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

/* ─── Hooks ─────────────────────────────────────────── */
export const useGetUploadsByParent = (token, orgId, parentId) =>
  useQuery({
    queryKey: ["dns", "uploads", orgId, parentId],
    queryFn: () => fetchUploadsByParent(token, orgId, parentId),
    enabled: !!token && !!orgId && parentId !== undefined && parentId !== null,
  });

export const useMoveUpload = (token, orgId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, uploadId, newParentId }) => moveUpload({ token, type, uploadId, newParentId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dnsKeys.folderTree(orgId) });
      qc.invalidateQueries({ queryKey: ["dns", "uploads", orgId] });
    },
  });
};

/* ─── Mutation for Copy Upload ──────────────────────── */
export const copyUpload = async ({ token, type, uploadId, newParentId }) => {
  const { data } = await axios.post(
    `${DNS_COPY_UPLOAD}?Type=${type}&UploadID=${uploadId}&NewParentID=${newParentId}`, null,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const useCopyUpload = (token, orgId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, uploadId, newParentId }) => copyUpload({ token, type, uploadId, newParentId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dnsKeys.folderTree(orgId) });
      qc.invalidateQueries({ queryKey: ["dns", "uploads", orgId] });
    },
  });
};

/* ─── Upload Files ──────────────────────────────────── */
/**
 * Upload one or more files to a specific org + parent folder.
 * The API expects multipart/form-data with a "Files" field for each file.
 */
export const uploadFiles = async ({ token, orgId, parentId, files }) => {
  const formData = new FormData();
  Array.from(files).forEach((file) => formData.append("Files", file));
  const { data } = await axios.post(
    `${DNS_UPLOAD_FILES}?OrganizationsDNSId=${orgId}&ParentID=${parentId}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return data;
};

export const useUploadFiles = (token, orgId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ parentId, files }) => uploadFiles({ token, orgId, parentId, files }),
    onSuccess: (_data, variables) => {
      // Invalidate the uploads query for the current parent so the list refreshes
      qc.invalidateQueries({ queryKey: ["dns", "uploads", orgId, variables.parentId] });
      qc.invalidateQueries({ queryKey: ["dns", "uploads", orgId] });
    },
  });
};

/* ─── Rename Upload (Folder or File) ────────────────── */
export const renameUpload = async ({ token, type, uploadId, fileName }) => {
  const { data } = await axios.post(
    `${DNS_RENAME_UPLOAD}?Type=${type}&UploadID=${uploadId}&FileName=${encodeURIComponent(fileName)}`, null,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const useRenameUpload = (token, orgId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, uploadId, fileName }) => renameUpload({ token, type, uploadId, fileName }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["dns", "uploads", orgId] });
      qc.invalidateQueries({ queryKey: dnsKeys.folderTree(orgId) });
    },
  });
};

/* ─── Delete Upload (Folder or File) ────────────────── */
export const deleteUpload = async ({ token, payload }) => {
  const { data } = await axios.post(
    DNS_DELETE_UPLOAD, payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const useDeleteUpload = (token, orgId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payload }) => deleteUpload({ token, payload }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["dns", "uploads", orgId] });
      qc.invalidateQueries({ queryKey: dnsKeys.folderTree(orgId) });
      qc.invalidateQueries({ queryKey: ["dns", "trashData", orgId] });
    },
  });
};

/* ─── Lock Upload (File/Folder) ─────────────────────── */
export const updateUploadLock = async ({ token, uploadId, isLocked }) => {
  const { data } = await axios.post(
    `${DNS_UPDATE_UPLOAD_LOCK}?UploadID=${uploadId}&IsLocked=${isLocked}`,
    null,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const useUpdateUploadLock = (token, orgId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uploadId, isLocked }) => updateUploadLock({ token, uploadId, isLocked }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dns", "uploads", orgId] });
      qc.invalidateQueries({ queryKey: dnsKeys.folderTree(orgId) });
    },
  });
};

/* ─── Update Upload Status (Publish/Draft) ──────────── */
export const updateUploadStatus = async ({ token, uploadId, status }) => {
  const { data } = await axios.post(
    `${DNS_UPDATE_UPLOAD_STATUS}?UploadID=${uploadId}&Status=${encodeURIComponent(status)}`,
    null,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const useUpdateUploadStatus = (token, orgId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uploadId, status }) => updateUploadStatus({ token, uploadId, status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dns", "uploads", orgId] });
      qc.invalidateQueries({ queryKey: dnsKeys.folderTree(orgId) });
    },
  });
};

/* ─── Update Upload Tags ────────────────────────────── */
export const updateUploadTags = async ({ token, uploadId, tags }) => {
  const { data } = await axios.post(
    `${DNS_UPDATE_UPLOAD_TAGS}?UploadID=${uploadId}&Tags=${encodeURIComponent(tags)}`,
    null,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

export const useUpdateUploadTags = (token, orgId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uploadId, tags }) => updateUploadTags({ token, uploadId, tags }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dns", "uploads", orgId] });
      qc.invalidateQueries({ queryKey: dnsKeys.folderTree(orgId) });
    },
  });
};

/* ─── Assigned Users Operations ────────────────────── */
export const fetchAssignedUsers = async (token, orgId, folderDNSID = null) => {
  const payload = {
    organizationsDNSId: orgId,
    operation: "GetAllAssignedUsers",
  };
  if (folderDNSID) payload.folderDNSID = folderDNSID;
  const { data } = await axios.post(
    DNS_ASSIGN_USER,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data?.data ?? [];
};

export const fetchUsersList = async (token, currentUserId) => {
  const { data } = await axios.get(`${DNS_GET_USERS}?UserID=${currentUserId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data?.data ?? [];
};

export const mutateAssignedUser = async ({ token, payload }) => {
  const { data } = await axios.post(DNS_ASSIGN_USER, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

export const useGetAssignedUsers = (token, orgId, folderDNSID = null) =>
  useQuery({
    queryKey: ["dns", "assignedUsers", orgId, folderDNSID || 0],
    queryFn: () => fetchAssignedUsers(token, orgId, folderDNSID),
    enabled: !!token && !!orgId,
  });

export const useGetUsersList = (token, currentUserId) =>
  useQuery({
    queryKey: ["dns", "usersList", currentUserId],
    queryFn: () => fetchUsersList(token, currentUserId),
    enabled: !!token && !!currentUserId,
  });

export const useMutateAssignedUser = (token, orgId, folderDNSID = null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => mutateAssignedUser({ token, payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dns", "assignedUsers", orgId, folderDNSID || 0] });
      qc.invalidateQueries({ queryKey: ["dns", "assignedUsers", orgId, 0] });
    },
  });
};

/* ─── Search Files/Folders ──────────────────────────── */
export const fetchSearchDetails = async (token, orgId, searchFileName) => {
  const { data } = await axios.get(
    `${DNS_SEARCH_DETAILS}?OrganizationsDNSId=${orgId}&SearchFileName=${encodeURIComponent(searchFileName || 'null')}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};
/* ─── Trash Data Operations ────────────────────────── */
export const fetchTrashData = async (token, orgId) => {
  const { data } = await axios.get(
    `${DNS_GET_ALL_TRASH_DATA}?OrganizationsDNSId=${orgId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data?.data ?? [];
};

export const deleteForeverUpload = async ({ token, payload }) => {
  const { data } = await axios.post(DNS_DELETE_FOREVER_UPLOAD, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

export const useGetTrashData = (token, orgId) =>
  useQuery({
    queryKey: ["dns", "trashData", orgId],
    queryFn: () => fetchTrashData(token, orgId),
    enabled: !!token && !!orgId,
  });

export const useDeleteForeverUpload = (token, orgId) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => deleteForeverUpload({ token, payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dns", "trashData", orgId] });
      qc.invalidateQueries({ queryKey: dnsKeys.folderTree(orgId) });
      qc.invalidateQueries({ queryKey: ["dns", "uploads", orgId] });
    },
  });
};

